import { useState, type FormEvent } from "react";
import { Bot } from "lucide-react";
import { Button } from "@web/components/ui/button";
import { Input } from "@web/components/ui/input";
import { createAgentSourcePlan, type AgentSourcePlan, type ContentKind } from "../api";

const defaultAgentContentKind: ContentKind = "novel";

export function AgentPage() {
  const [targetUrl, setTargetUrl] = useState("https://example.com/books");
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [plan, setPlan] = useState<AgentSourcePlan | null>(null);
  const [message, setMessage] = useState("当前只生成计划，不调用真实 LLM");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("正在生成接口计划");

    try {
      const nextPlan = await createAgentSourcePlan({ targetUrl, kind: defaultAgentContentKind, requiresLogin });
      setPlan(nextPlan);
      setMessage(`已生成 ${nextPlan.candidateSourceId} 的书源开发计划`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Bot className="size-4" />
            LLM 书源工具接口
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">当前只保留接口和任务边界，不执行真实 LLM 调用</p>
        </div>
        <div className="space-y-3 border-y py-3">
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input value={targetUrl} onChange={event => setTargetUrl(event.target.value)} placeholder="目标网站 URL" />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={requiresLogin} onChange={event => setRequiresLogin(event.target.checked)} />
              目标站点可能需要登录
            </label>
            <Button type="submit" disabled={busy || !targetUrl.trim()}>
              生成计划
            </Button>
          </form>
          <p className="text-xs text-muted-foreground">{message}</p>
        </div>
      </section>

      {plan && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">
              {plan.displayName} · {plan.candidateSourceId}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{plan.targetUrl}</p>
          </div>
          <div className="space-y-4 border-y py-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {plan.steps.map(step => (
                <div key={step.id} className="border-b py-3 text-sm last:border-b-0">
                  <p className="font-semibold">{step.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {step.stage} · {step.tool}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">{step.purpose}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Acceptance</p>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {plan.acceptanceChecks.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
